import { useState } from "react";
import { ADMIN_ORGS } from "../../lib/paths.js";
import { useAtomValue } from "jotai";
import { useNavigate, useOutletContext } from "react-router-dom";
import { apiCall } from "../../lib/api-client.js";
import { ApiError, type Organization } from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import type { OrgDetailContext } from "./context.js";
import { Spinner } from "../../components/Spinner.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { formatDateTime } from "../../lib/format.js";

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="space-y-1">
      <dt className="text-xs uppercase tracking-wide text-dosia-text-secondary">
        {label}
      </dt>
      <dd className="text-sm text-dosia-text">{children}</dd>
    </div>
  );
}

export function BasicInfo(): JSX.Element | null {
  const { org, setOrg: onPatched } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  const navigate = useNavigate();
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(org.name);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  if (!auth) return null;

  // org-admin 全字段只读：名称/状态/员工上限/到期日均为平台商务属性，
  // 写路径（PATCH/DELETE /api/organizations/:code）服务端 requireSuper 403。
  const readOnly = auth.scope === "org-admin";

  const patch = async (body: Partial<Organization>): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      const next = await apiCall<Organization>(
        "PATCH",
        `/api/organizations/${org.code}`,
        { body, token: auth.token },
      );
      onPatched(next);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "保存失败";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const saveName = async (): Promise<void> => {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === org.name) {
      setEditingName(false);
      setNameInput(org.name);
      return;
    }
    await patch({ name: trimmed });
    setEditingName(false);
  };

  const toggleSuspend = async (): Promise<void> => {
    const next = org.status === "active" ? "suspended" : "active";
    await patch({ status: next });
  };

  const archive = async (): Promise<void> => {
    if (!confirmArchive) {
      setConfirmArchive(true);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await apiCall("DELETE", `/api/organizations/${org.code}`, {
        token: auth.token,
      });
      navigate(ADMIN_ORGS, { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "归档失败";
      setError(msg);
      setConfirmArchive(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {error ? <ErrorBanner onDismiss={() => setError(null)}>{error}</ErrorBanner> : null}

      <div className="rounded-lg border border-dosia-border bg-dosia-bg-light p-5">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="代号">
            <span className="font-mono">{org.code}</span>
          </Field>
          <Field label="名称">
            {editingName ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={nameInput}
                  autoFocus
                  onChange={(e) => setNameInput(e.target.value)}
                  className="flex-1 rounded border border-dosia-border bg-dosia-bg-input px-2 py-1 text-sm focus:border-dosia-accent focus:outline-none"
                />
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void saveName()}
                  className="rounded bg-dosia-accent px-2 py-1 text-xs text-dosia-bg-light hover:bg-dosia-accent-hover disabled:opacity-60"
                >
                  保存
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingName(false);
                    setNameInput(org.name);
                  }}
                  className="rounded border border-dosia-border px-2 py-1 text-xs text-dosia-text-secondary hover:bg-dosia-bg-lighter"
                >
                  取消
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span>{org.name}</span>
                {readOnly ? null : (
                  <button
                    type="button"
                    onClick={() => setEditingName(true)}
                    className="text-xs text-dosia-accent-700 hover:underline"
                  >
                    编辑
                  </button>
                )}
              </div>
            )}
          </Field>
          <Field label="状态">{org.status}</Field>
          <Field label="员工上限">{org.maxEmployees}</Field>
          <Field label="到期日">{formatDateTime(org.expiresAt)}</Field>
          <Field label="ByteSpike 组织代码">
            {org.dosiaProvisioning?.bytespikeOrgCode ?? "—"}
          </Field>
          <Field label="创建时间">{formatDateTime(org.createdAt)}</Field>
          <Field label="最近更新">{formatDateTime(org.updatedAt)}</Field>
        </dl>
      </div>

      {readOnly ? (
        <p className="text-xs text-dosia-text-dim">
          状态 / 员工上限 / 到期日等由 DOSIA 平台管理，如需调整请联系运营。
        </p>
      ) : (
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy || org.status === "archived"}
          onClick={() => void toggleSuspend()}
          className="rounded border border-dosia-border px-3 py-1.5 text-sm text-dosia-text hover:bg-dosia-bg-lighter disabled:opacity-60"
        >
          {org.status === "active" ? "暂停" : "恢复"}
        </button>
        <button
          type="button"
          disabled={busy || org.status === "archived"}
          onClick={() => void archive()}
          className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-sm text-amber-800 hover:bg-amber-500/20 disabled:opacity-60"
        >
          {confirmArchive ? "确认归档（不可逆）" : "归档"}
        </button>
        {confirmArchive ? (
          <button
            type="button"
            onClick={() => setConfirmArchive(false)}
            className="text-xs text-dosia-text-secondary hover:underline"
          >
            取消
          </button>
        ) : null}
        {busy ? <Spinner size="sm" /> : null}
      </div>
      )}
    </div>
  );
}
